// Serviço de integração com balanças comerciais via Web Serial API
// Suporta: Toledo, Filizola, Urano, Balmak, Elgin

const BALANCA_CONFIGS = {
  toledo_prix: {
    name: 'Toledo Prix',
    baudRate: 9600,
    dataBits: 8,
    stopBits: 1,
    parity: 'none',
    // Toledo Prix envia peso em gramas (5-6 dígitos) OU em kg com vírgula/ponto.
    parseWeight: (data) => {
      const clean = String(data).replace(/[^0-9.,]/g, '');
      if (!clean) return null;
      // Formato com decimal (ex.: "0.500", "12,345") = kg
      const decMatch = clean.match(/(\d+[.,]\d+)/);
      if (decMatch) return parseFloat(decMatch[1].replace(',', '.'));
      // Formato inteiro (ex.: "00500", "12345") em gramas → converte pra kg
      const intMatch = clean.match(/(\d{3,7})/);
      if (intMatch) return parseFloat(intMatch[1]) / 1000;
      return null;
    }
  },
  filizola: {
    name: 'Filizola',
    baudRate: 4800,
    dataBits: 8,
    stopBits: 1,
    parity: 'none',
    parseWeight: (data) => {
      // Formato Filizola: geralmente "PPPPP" ou com prefixo
      const clean = data.replace(/[^\d.]/g, '');
      const match = clean.match(/(\d{1,6})/);
      if (match) {
        const peso = parseFloat(match[1]);
        return peso > 100 ? peso / 1000 : peso; // Se > 100, assume gramas
      }
      return null;
    }
  },
  urano: {
    name: 'Urano',
    baudRate: 9600,
    dataBits: 8,
    stopBits: 1,
    parity: 'none',
    parseWeight: (data) => {
      // Urano usa STX (0x02) e ETX (0x03) como delimitadores
      const clean = data.replace(/[\x02\x03\r\n]/g, '');
      const match = clean.match(/(\d+[.,]?\d*)/);
      if (match) {
        return parseFloat(match[1].replace(',', '.'));
      }
      return null;
    }
  },
  balmak: {
    name: 'Balmak',
    baudRate: 9600,
    dataBits: 8,
    stopBits: 1,
    parity: 'none',
    parseWeight: (data) => {
      // Balmak: formato similar ao Toledo
      const match = data.match(/(\d{4,6})/);
      if (match) {
        return parseFloat(match[1]) / 1000;
      }
      return null;
    }
  },
  elgin: {
    name: 'Elgin',
    baudRate: 9600,
    dataBits: 8,
    stopBits: 1,
    parity: 'none',
    parseWeight: (data) => {
      // Elgin DP/SA: formato "PPPPPP" em gramas
      const clean = data.replace(/[^\d]/g, '');
      if (clean.length >= 4) {
        return parseFloat(clean) / 1000;
      }
      return null;
    }
  },
  generica: {
    name: 'Genérica (Auto-detectar)',
    baudRate: 9600,
    dataBits: 8,
    stopBits: 1,
    parity: 'none',
    parseWeight: (data) => {
      // Tenta detectar automaticamente o formato
      const clean = data.replace(/[^\d.,]/g, '');
      
      // Se tem ponto ou vírgula, assume kg
      if (clean.includes('.') || clean.includes(',')) {
        const match = clean.match(/(\d+[.,]\d+)/);
        if (match) {
          return parseFloat(match[1].replace(',', '.'));
        }
      }
      
      // Se é número inteiro, assume gramas
      const match = clean.match(/(\d{3,6})/);
      if (match) {
        const valor = parseFloat(match[1]);
        return valor > 100 ? valor / 1000 : valor;
      }
      
      return null;
    }
  }
};

class BalancaService {
  constructor() {
    this.port = null;
    this.reader = null;
    this.isConnected = false;
    this.currentConfig = BALANCA_CONFIGS.generica;
    this.onWeightChange = null;
    this.onConnectionChange = null;
    this.onError = null; // Callback para erros (libera input manual)
    this.lastWeight = 0;
    this.buffer = '';
    this.hasError = false; // Flag de erro para liberar input manual
    this.savedConfigType = null; // Tipo salvo no banco
    this.abortController = null; // Controller para abortar stream
  }

  // Verifica se Web Serial API está disponível
  isSupported() {
    return 'serial' in navigator;
  }

  // Lista portas já autorizadas (sem prompt)
  async getAuthorizedPorts() {
    if (!this.isSupported()) return [];
    return await navigator.serial.getPorts();
  }

  // Tenta reconectar automaticamente usando porta já autorizada
  async autoConnect(configName = 'generica', customBaudRate = null) {
    if (!this.isSupported()) {
      return { success: false, reason: 'not_supported' };
    }

    try {
      // Pega portas já autorizadas
      const ports = await this.getAuthorizedPorts();
      
      if (ports.length === 0) {
        return { success: false, reason: 'no_authorized_ports' };
      }

      // Tenta a primeira porta autorizada
      this.port = ports[0];
      this.currentConfig = BALANCA_CONFIGS[configName] || BALANCA_CONFIGS.generica;
      this.savedConfigType = configName;
      
      // Usa baud rate customizado se fornecido
      const baudRate = customBaudRate || this.currentConfig.baudRate;
      
      // Tenta abrir a porta
      await this.port.open({
        baudRate: baudRate,
        dataBits: this.currentConfig.dataBits,
        stopBits: this.currentConfig.stopBits,
        parity: this.currentConfig.parity
      });

      this.isConnected = true;
      this.hasError = false;
      this.onConnectionChange?.(true, this.currentConfig.name);
      
      // Inicia leitura contínua
      this.startReading();
      
      return { success: true };
    } catch (error) {
      console.log('Auto-conexão falhou:', error.message);
      this.hasError = true;
      return { success: false, reason: 'connection_failed', error: error.message };
    }
  }

  // Conecta à balança (com prompt de seleção de porta)
  async connect(configName = 'generica', customBaudRate = null) {
    if (!this.isSupported()) {
      throw new Error('Web Serial API não suportada neste navegador. Use Chrome ou Edge.');
    }

    try {
      // Solicita seleção de porta ao usuário
      this.port = await navigator.serial.requestPort();
      
      // Obtém configuração da balança
      this.currentConfig = BALANCA_CONFIGS[configName] || BALANCA_CONFIGS.generica;
      this.savedConfigType = configName;
      
      // Usa baud rate customizado se fornecido
      const baudRate = customBaudRate || this.currentConfig.baudRate;
      
      // Abre a porta com as configurações
      await this.port.open({
        baudRate: baudRate,
        dataBits: this.currentConfig.dataBits,
        stopBits: this.currentConfig.stopBits,
        parity: this.currentConfig.parity
      });

      this.isConnected = true;
      this.hasError = false;
      this.onConnectionChange?.(true, this.currentConfig.name);
      
      // Inicia leitura contínua
      this.startReading();
      
      return true;
    } catch (error) {
      console.error('Erro ao conectar balança:', error);
      this.isConnected = false;
      this.hasError = true;
      this.onConnectionChange?.(false, null);
      throw error;
    }
  }

  // Desconecta da balança
  async disconnect() {
    try {
      // Primeiro, marca como desconectado para parar novas leituras
      this.isConnected = false;

      // 🆕 Para o polling de ENQ
      if (this._pollingInterval) {
        clearInterval(this._pollingInterval);
        this._pollingInterval = null;
      }
      
      // Aborta o stream pipe se existir
      if (this.abortController) {
        try {
          this.abortController.abort();
        } catch (abortError) {
          // Ignora erro de abort
        }
        this.abortController = null;
      }
      
      // Cancela o reader se existir
      if (this.reader) {
        try {
          await this.reader.cancel();
        } catch (readerError) {
          // Ignora erro de cancelamento (pode já estar fechado)
          console.log('Reader já estava fechado ou cancelado');
        }
        this.reader = null;
      }
      
      // Pequeno delay para garantir que streams foram fechados
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Fecha a porta se existir
      if (this.port) {
        try {
          // Verifica se a porta está aberta antes de fechar
          await this.port.close();
        } catch (portError) {
          // Ignora erro de fechamento (pode já estar fechada)
          console.log('Porta já estava fechada ou erro ao fechar:', portError.message);
        }
        this.port = null;
      }
      
      // Notifica mudança de conexão
      if (typeof this.onConnectionChange === 'function') {
        this.onConnectionChange(false, null);
      }
    } catch (error) {
      console.error('Erro ao desconectar:', error);
      // Garante que o estado é atualizado mesmo com erro
      this.isConnected = false;
      this.reader = null;
      this.port = null;
      this.abortController = null;
      if (typeof this.onConnectionChange === 'function') {
        this.onConnectionChange(false, null);
      }
    }
  }

  // Inicia leitura contínua da balança
  async startReading() {
    if (!this.port || !this.port.readable) return;

    try {
      const decoder = new TextDecoderStream();
      
      // Guarda referência para poder abortar depois
      this.abortController = new AbortController();
      
      // pipeTo retorna uma promise que resolve quando o stream termina
      // Não esperamos aqui, mas tratamos erros
      this.port.readable.pipeTo(decoder.writable, { signal: this.abortController.signal })
        .catch(err => {
          // Ignora erro de abort (é esperado ao desconectar)
          if (err.name !== 'AbortError') {
            console.log('Stream pipe encerrado:', err.message);
          }
        });
      
      const inputStream = decoder.readable;
      this.reader = inputStream.getReader();

      // 🆕 Polling periódico: envia ENQ (0x05) a cada 400ms para balanças
      // que só respondem sob demanda (Toledo Prix 3, alguns Filizola). Para
      // balanças que enviam continuamente, o ENQ é ignorado.
      this._pollingInterval = setInterval(() => {
        if (this.isConnected && this.port && this.port.writable) {
          this.requestWeight().catch(() => { /* silencioso */ });
        }
      }, 400);

      while (this.isConnected) {
        try {
          const { value, done } = await this.reader.read();
          
          if (done || !this.isConnected) {
            break;
          }
          
          if (value) {
            this.hasError = false; // Leitura OK, remove flag de erro
            this.processData(value);
          }
        } catch (readError) {
          // Erro de leitura individual - não quebra o loop necessariamente
          if (this.isConnected) {
            console.log('Erro na leitura:', readError.message);
          }
          break;
        }
      }
    } catch (error) {
      // Ignora erros esperados durante desconexão
      if (this.isConnected && error.name !== 'TypeError' && error.name !== 'AbortError') {
        console.error('Erro na leitura:', error);
        this.hasError = true;
        if (typeof this.onError === 'function') {
          this.onError(error.message);
        }
      }
    } finally {
      // Limpa polling ao sair do loop
      if (this._pollingInterval) {
        clearInterval(this._pollingInterval);
        this._pollingInterval = null;
      }
    }
  }

  // Processa dados recebidos da balança
  processData(data) {
    try {
      this.buffer += data;

      // Muitas balanças usam delimitadores variados: \r\n, \r, \n, STX/ETX (0x02/0x03).
      // Split cobre TODOS os casos comuns.
      const lines = this.buffer.split(/[\r\n\x02\x03]+/);

      // Processa todas as linhas completas
      for (let i = 0; i < lines.length - 1; i++) {
        const line = lines[i].trim();
        if (line) {
          this._tryEmit(line);
        }
      }

      // Mantém o último fragmento no buffer
      this.buffer = lines[lines.length - 1];

      // 🆕 Se o buffer parou de crescer com delimitadores (balança que só
      // envia peso repetidamente sem CR/LF), tenta parsear o buffer inteiro
      // quando ele já tem tamanho de mensagem plausível (>= 4 chars).
      if (this.buffer.length >= 4 && this.buffer.length <= 32) {
        // Tenta emitir SEM zerar o buffer (senão perde dados parciais); se
        // parsear ok, atualiza o lastWeight — a próxima leitura completa
        // aparece com CR/LF ou o loop natural continua.
        const trimmed = this.buffer.trim();
        if (trimmed) this._tryEmit(trimmed);
      }

      // Limita tamanho do buffer para evitar leaks
      if (this.buffer.length > 200) {
        this.buffer = this.buffer.slice(-100);
      }
    } catch (error) {
      console.error('Erro ao processar dados da balança:', error);
      this.hasError = true;
      if (typeof this.onError === 'function') {
        this.onError(error.message);
      }
    }
  }

  // Chama o parser da balança selecionada. Sempre dispara onWeightChange
  // (mesmo se o peso não mudou) — assim que o usuário troca de produto,
  // o campo quantidade é preenchido imediatamente.
  _tryEmit(line) {
    try {
      const weight = this.currentConfig.parseWeight(line);
      if (weight !== null && !isNaN(weight) && weight >= 0) {
        this.lastWeight = weight;
        if (typeof this.onWeightChange === 'function') {
          this.onWeightChange(weight);
        }
      }
    } catch (_err) {
      /* parse errors silenciados por linha; a próxima leitura tenta de novo */
    }
  }

  // Solicita peso (para balanças que precisam de comando)
  async requestWeight() {
    if (!this.port || !this.port.writable) return;
    
    const encoder = new TextEncoder();
    const writer = this.port.writable.getWriter();
    
    try {
      // Comando comum para solicitar peso (ENQ - 0x05)
      await writer.write(encoder.encode('\x05'));
    } finally {
      writer.releaseLock();
    }
  }

  // Verifica se permite input manual (erro na balança ou não conectada)
  allowManualInput() {
    return !this.isConnected || this.hasError;
  }

  // Obtém lista de configurações disponíveis
  static getAvailableConfigs() {
    return Object.entries(BALANCA_CONFIGS).map(([key, config]) => ({
      value: key,
      label: config.name
    }));
  }
}

// Instância singleton
const balancaService = new BalancaService();

export { balancaService, BALANCA_CONFIGS };
export default balancaService;
